import React, { createContext, useContext, useMemo } from 'react'

import { getCurrentFeatureFlags } from 'api/auth'
import {
    FEATURE_FLAGS_SWR_KEY,
    FlagFormat,
} from 'api/auth/getCurrentFeatureFlags'
import useUser from 'hooks/useUser'
import useSWR, { useSWRConfig } from 'swr'

export enum FeatureFlags {
    akHiStateEnabled = 'cap-388-enable-ak-hi-states',
    dealTermsTable = 'ABBA-1974-show_new_terms_table_in_work_the_deal_for_powersport_and_rv',
    dealTermsTableExtendedLogging = 'ABBA-3112-enable-deal-terms-table-extended-logging',
    dealTermsTableFiltering = 'BLUR-459-add-filters-to-available-deal-terms',
    dealershipLenderAgreementValidation = 'FIKA-3041-dealership-lender-agreement-submission-validation',
    documentViewerForStips = 'SHOCK-247-enable-document-viewer-for-stips-on-dealer-portal',
    enableHnbIntegration = 'CORE-5847-enable-hnb-integration',
    enableOpeAssetExpansion = 'CORE-9064-enable-ope-asset-expansion',
    enableOpeMultiCollateralUi = 'CORE-9570-enable-ope-multi-collateral-ui',
    enableOpeSearchAsYouType = 'CORE-9285-enable-ope-search-as-you-type',
    invoicePriceForNewRv = 'RV-263-invoice-price-for-new-rv',
    leadsPage = 'ROGUE-270-enable-leads-page-for-dealer-portal',
    marineShowSubmitDifferentSwitch = 'BLUR-819-show-submit-different-for-marine',
    marineWtdModernization = 'DP-217-marine-wtd-modernization',
    automobileWtdReact = 'NV-27-automobile-wtd-react',
    enableSelfServiceDealAdjustment = 'NV-395-enable-self-service-deal-adjustment',
    displayPromotionContractingBanner = 'NV-623-display-promotion-contracting-banner',
    modernizedWtdContractingFlow = 'ABBA-4177-wtd-modernization-contracting-flow',
    modernizedWtdContractingSection = 'ABBA-4173-wtd-modernization-contract-section',
    modernizedWtdContractingSectionApiUsage = 'ABBA-4201-wtd-modernization-itemization-section-api-usage',
    modernizedRvDecisionPage = 'CP-345-modernized-rv-decision-page',
    modernizedRvWtd = 'CP-201-modernized-rv-wtd',
    newRvApp = 'BLUR-777-enable-new-rv-application',
    newWTDFinancingSummary = 'FIKA-4236-wtd-modernization-financing-summary',
    newWTDItemizationSection = 'ABBA-3651-wtd-modernization-itemization-section',
    newWtdPath = 'ABBA-1454-determine-wtd-path',
    newWtdStips = 'FIKA-3873-wtd-modernization-stips',
    offersTableUpdatesPhase1 = 'cp-353-offers-table-updates-phase-1',
    offersTableUpdatesPhase2 = 'cp-361-offers-table-v2-phase-2',
    oemRebatePowerSports = 'ABBA-3007-enable-oem-rebate',
    autosRebate = 'NV-872-enable-autos-rebate',
    powersportsDownPaymentCapPilot = 'CORE-6964-powersports-down-payment-cap-pilot',
    returnCustomerPsApp2 = 'FIKA-2643-return-customer-ps-app-2-0',
    routePsAppToNewReadOnlyPage = 'FIKA-3222-dealer-ps-app-read-only-page',
    routeRvAppToNewReadOnlyPage = 'CP-245-dealer-rv-app-read-only-page',
    salesPersonFlexLink = 'DITTO-943-enable-salesperson-flex-link-dealer-portal',
    showFlexLink = 'DITTO-690-show-flex-link-homepage',
    showNewSafeCheckPage = 'ABBA-2032-new-safecheck-report-page',
    creditAccessReportPage = 'CP-253-enable-credit-access-report-v2',
    safecheckV2 = 'CP-192-safecheck-v2-flag',
    useVantageScoreForSafecheck = 'CP-386-use-vantage-score-for-safecheck',
    showRVCrossoverExperience = 'BLUR-971-rv-crossover-experience',
    showRePowerTile = 'BLUR-658-show-repower-tile',
    singleBucket = 'FIKA-3637-single-bucket',
    enableBuffaloHaggling = 'CAP-452-enable-buffalo-haggling',
    hideWtdShowAllOffersCheckbox = 'CAP-841-hide-wtd-show-all-offers-checkbox',
    enableContractExpiration = 'CORE-9013-enable-contract-expiration',
    wisconsinMaritalDisclosureCatchAll = 'SEN-1372-wisconsin-marital-disclosure-catch-all',
    useDealershipStateForRrfPowersports = 'CORE-9463-use-dealership-state-for-rrf-powersports',
    stipDownloadsDisabled = 'SEN-1514-disable-stip-downloads',
    enableTrueStepRate = 'TRUE-STEP-RATE-enable-true-step-rate',
}

type FeatureFlagsMap = {
    [K in keyof typeof FeatureFlags]?: boolean | undefined
}

export type FeatureFlagContextInterface = FeatureFlagsMap & {
    flagsLoaded?: boolean
    handleShouldUpdateFlags?: () => void
    [K: string]: boolean | (() => void) | undefined
}

export const FeatureFlagContext =
    createContext<FeatureFlagContextInterface | null>(null)

interface FeatureFlagContextProviderProps {
    readonly children: React.ReactNode
}

const EMPTY_FLAGS: FlagFormat = { flags: {}, switches: {} }

const setFlagValues = (flags: FlagFormat): FeatureFlagsMap =>
    Object.entries(FeatureFlags)
        .map(([key, flagName]) => {
            const flagValue =
                flags.flags?.[flagName] || flags.switches?.[flagName]

            const isActive = flagValue?.is_active ?? false

            return { [key]: isActive }
        })
        .reduce((acc, curr) => ({ ...acc, ...curr }), {})

function FeatureFlagContextProvider({
    children,
}: FeatureFlagContextProviderProps) {
    const { isLoggedIn } = useUser()
    const { mutate } = useSWRConfig()

    const { data, isLoading } = useSWR(
        isLoggedIn ? FEATURE_FLAGS_SWR_KEY : null,
        getCurrentFeatureFlags,
        {
            errorRetryCount: 0,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
        },
    )

    const flags = useMemo(() => setFlagValues(data ?? EMPTY_FLAGS), [data])

    const ctx = useMemo(
        () => ({
            ...flags,
            flagsLoaded: !isLoading,
            handleShouldUpdateFlags: () => {
                mutate(FEATURE_FLAGS_SWR_KEY)
            },
        }),

        [flags, isLoading, mutate],
    )

    return (
        <FeatureFlagContext.Provider value={ctx}>
            {children}
        </FeatureFlagContext.Provider>
    )
}

export default FeatureFlagContextProvider

export const useFeatureFlagContext = () => useContext(FeatureFlagContext)
